/**
 * ID Card utility constants
 * Based on standard ID card dimensions: 85.60 × 53.98 mm
 */

/**
 * Aspect ratio of a standard ID card (85.60 mm width / 53.98 mm height)
 */
export const ID_CARD_ASPECT_RATIO = 85.60 / 53.98;

/**
 * Frame width percentage (90% of device width)
 */
const FRAME_WIDTH_PERCENTAGE = 0.9;

/**
 * Maximum frame width in pixels
 */
const MAX_FRAME_WIDTH = 600;

/**
 * Calculates the free vertical space available after placing the frame.
 * 
 * Steps:
 * 1. Estimates the height of the frame based on device width
 * 2. Calculates free space = device height - estimated frame height
 * 
 * @param deviceWidth - The width of the device/viewport in pixels
 * @param deviceHeight - The height of the device/viewport in pixels
 * @returns The free vertical space in pixels (device height - frame height)
 */
export const calculateFreeSpace = (deviceWidth: number, deviceHeight: number): number => {
    // Calculate frame width: 90% of device width, but capped at 600px max
    const frameWidth = Math.min(deviceWidth * FRAME_WIDTH_PERCENTAGE, MAX_FRAME_WIDTH);

    // Estimate frame height using the ID card aspect ratio
    // Since aspectRatio = width / height, we get: height = width / aspectRatio
    const estimatedFrameHeight = frameWidth / ID_CARD_ASPECT_RATIO;

    // Calculate free space: device height - estimated frame height
    const freeSpace = deviceHeight - estimatedFrameHeight;

    return freeSpace;
};

/**
 * Crops the captured photo by removing 80% of the free space from top and bottom.
 * 
 * Formula:
 * 1. Calculate free space = device height - estimated frame height
 * 2. Divide free space by 2 = free space from top/bottom
 * 3. Take 80% of that = pixels to crop from top and bottom
 * 4. Crop the image accordingly
 * 
 * @param imageDataUrl - The captured photo as a data URL (base64 string)
 * @param deviceWidth - The width of the device/viewport in pixels
 * @param deviceHeight - The height of the device/viewport in pixels
 * @returns Promise that resolves to the cropped image as a data URL
 */
export const cropPhotoTopBottom = (
    imageDataUrl: string,
    deviceWidth: number,
    deviceHeight: number
): Promise<string> => {
    return new Promise((resolve, reject) => {
        // Calculate free space
        const freeSpace = calculateFreeSpace(deviceWidth, deviceHeight);

        // Divide by 2 to get free space from top/bottom
        const freeSpaceTopBottom = freeSpace / 2;

        // Take 80% of that = pixels to crop from top and bottom
        const cropPixels = freeSpaceTopBottom * 0.8;

        // Create image element to load the data URL
        const img = new Image();
        img.onload = () => {
            // Calculate the scale factor between device height and actual image height
            // This ensures we crop proportionally based on the actual image size
            const scaleY = img.height / deviceHeight;

            // Scale the crop pixels to match the actual image dimensions
            const scaledCropPixels = cropPixels * scaleY;

            // Calculate the cropped height
            const croppedHeight = img.height - (scaledCropPixels * 2);

            // Ensure we don't crop more than the image height
            if (croppedHeight <= 0) {
                reject(new Error('Crop amount exceeds image height'));
                return;
            }

            // Create a temporary hidden canvas (not added to DOM)
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            // Enable high-quality image smoothing
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Set canvas dimensions (full width, reduced height)
            canvas.width = img.width;
            canvas.height = croppedHeight;

            // Draw the cropped portion of the image
            // sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight
            ctx.drawImage(
                img,
                0, scaledCropPixels, // Start from top + scaled crop pixels
                img.width, croppedHeight, // Source: full width, cropped height
                0, 0, // Destination position
                canvas.width, canvas.height // Destination size
            );

            // Convert to data URL with maximum quality
            const croppedImageDataUrl = canvas.toDataURL('image/jpeg', 1.0);
            resolve(croppedImageDataUrl);
        };

        img.onerror = () => {
            reject(new Error('Failed to load image'));
        };

        img.src = imageDataUrl;
    });
};

/**
 * Maximum width/height for compression (images larger than this will be resized)
 * This helps reduce file size significantly for very large images
 */
const MAX_COMPRESSION_DIMENSION = 2000;

/**
 * JPEG quality for compression (0.85 provides good compression with minimal visible quality loss)
 * Range: 0.0 (lowest quality, smallest file) to 1.0 (highest quality, largest file)
 */
const COMPRESSION_QUALITY = 0.85;

/**
 * Compresses a photo using canvas to reduce file size while maintaining visual quality.
 * 
 * The compression uses:
 * - JPEG format with quality 0.85 (provides significant compression with minimal visible quality loss)
 * - Automatic resizing if image exceeds MAX_COMPRESSION_DIMENSION (maintains aspect ratio)
 * - High-quality image smoothing for better results
 * 
 * This compression is typically not visible to the human eye but can reduce file size by 60-80%.
 * 
 * @param imageDataUrl - The image as a data URL (base64 string)
 * @param maxDimension - Optional maximum width/height in pixels (default: 2000px)
 *                       Images larger than this will be resized proportionally
 * @returns Promise that resolves to the compressed image as a data URL
 */
export const compressPhoto = (
    imageDataUrl: string,
    maxDimension: number = MAX_COMPRESSION_DIMENSION
): Promise<string> => {
    return new Promise((resolve, reject) => {
        // Create image element to load the data URL
        const img = new Image();
        img.onload = () => {
            // Calculate dimensions (maintain aspect ratio if resizing needed)
            let width = img.width;
            let height = img.height;

            // Resize if image exceeds max dimension (maintains aspect ratio)
            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    // Landscape: scale by width
                    height = (height / width) * maxDimension;
                    width = maxDimension;
                } else {
                    // Portrait or square: scale by height
                    width = (width / height) * maxDimension;
                    height = maxDimension;
                }
            }

            // Create a temporary hidden canvas (not added to DOM)
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                reject(new Error('Could not get canvas context'));
                return;
            }

            // Enable high-quality image smoothing for better compression results
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Set canvas dimensions
            canvas.width = width;
            canvas.height = height;

            // Draw the image to canvas (this will resize if needed)
            ctx.drawImage(img, 0, 0, width, height);

            // Convert to data URL with compression quality
            // Using JPEG format with quality 0.85 provides significant compression
            // while maintaining visual quality that's not noticeable to the human eye
            const compressedImageDataUrl = canvas.toDataURL('image/jpeg', COMPRESSION_QUALITY);
            resolve(compressedImageDataUrl);
        };

        img.onerror = () => {
            reject(new Error('Failed to load image for compression'));
        };

        img.src = imageDataUrl;
    });
};

/**
 * Converts a data URL (base64 encoded image string) to a File object.
 * 
 * This is useful when you need to convert an image captured from canvas (via toDataURL)
 * into a File object that can be used with file upload APIs.
 * 
 * @param dataurl - The data URL string (e.g., "data:image/jpeg;base64,/9j/4AAQ...")
 * @param filename - The desired filename for the File object
 * @returns A File object containing the image data
 */
export const dataURLtoFile = (dataurl: string, filename: string): File => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/jpeg';
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};
